import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { UFOThirdPartySegment } from '@atlaskit/react-ufo/third-party-segment';
import type { IframeSegmentEvent } from '@atlaskit/react-ufo/third-party-segment';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ForgeUIExtensionAnalyticsContext } from '../../analytics';
import { extensionIdToAnalyticsAttributes } from '../../analytics/extensionIdToAnalyticsAttributes';
import { ForgeUIRuntimeAnalyticsContext } from '../../analytics/ForgeUIRuntimeAnalyticsContext';
import type { ExtensionIdParts } from '../../analytics/types';
import {
	makeFrame,
	makeModalThreeLOPromptForCustomUI,
	makeThreeLOPromptForCustomUI,
} from '../../components';
import { UserAccessProvider } from '../../context/user-access';
import {
	PerformanceAnalyticsProviderRollout,
	PerformanceAnalyticsContext,
} from '../../performance-profiling/PerformanceAnalyticsProvider';
import { PerformanceHoldAttributionAnchorRollout } from '../../performance-profiling/PerformanceHoldAttributionAnchor';
import { isCustomUI } from '../../utils/isCustomUI';
import { resolveDynamicResource } from '../../utils/resolveDynamicResource';
import { getForgeRuntimeType } from '../../web-runtime/getForgeRuntimeType';
import { CustomUIRenderer, ModalCustomUIRenderer } from './CustomUIRenderer';
import {
	type ForgeUIRendererProps,
	type ConfluenceCustomUIProps,
	type CloudOrWorkspaceId,
} from './types';
import { UIKitRenderer } from './UIKitRenderer';

type Props = ForgeUIRendererProps & CloudOrWorkspaceId & ConfluenceCustomUIProps;

/**
 * Inner wrapper rendered inside PerformanceAnalyticsProviderRollout so it can
 * access PerformanceAnalyticsContext and wire up UFOThirdPartySegment's iframe
 * behavior via onRegisterIframeEventListener.
 */
function UFOThirdPartySegmentWithPerfTimeline({
	children,
	name,
	extraData,
	moduleType,
	isBackgroundModule,
}: {
	children: React.ReactNode;
	extraData?: Record<string, string | undefined>;
	isBackgroundModule?: boolean;
	moduleType?: string;
	name: string;
}): React.JSX.Element {
	const timelineRef = useContext(PerformanceAnalyticsContext);

	const onRegisterIframeEventListener = useCallback(
		(listener: (event: IframeSegmentEvent) => void) => {
			const timeline = timelineRef?.current;
			if (!timeline) {
				return () => {};
			}
			timeline.listeners.add(listener);
			return () => {
				timeline.listeners.delete(listener);
			};
		},
		[timelineRef],
	);

	// Stable reference for the excluded-module breadcrumb so the segment's writer records it.
	// `extraData` is stable in practice (memoized upstream as `ufoSegmentExtraData`)
	const excludedData = useMemo(
		() => ({
			name,
			...extraData,
			excludedFromMetrics: true,
			reason: 'background-script',
		}),
		[name, extraData],
	);

	// Background modules render no visible UI but keep an iframe alive that would otherwise
	// register a long-lived `forge-ui-requests` third-party hold. That hold never cleanly releases,
	// deferring the interaction's third-party metric window until it is force-closed and
	// massively inflating `ttai_include_third_party`.
	// Render a plain third-party segment without the iframe timeline wiring, so no metric-affecting
	// hold is registered.
	//
	// Products own their module taxonomy and opt in by passing `isBackgroundModule`. The
	// `*:backgroundScript` suffix is a generic, product-agnostic fallback for callers that do not.
	const isBackground = isBackgroundModule || moduleType?.endsWith(':backgroundScript');
	if (isBackground && fg('platform_forge_ufo_exclude_bg_scripts_from_3p')) {
		return (
			<UFOThirdPartySegment name={name} excludeFromMetrics excludedData={excludedData}>
				{children}
			</UFOThirdPartySegment>
		);
	}

	return (
		<UFOThirdPartySegment
			name={name}
			onRegisterIframeEventListener={onRegisterIframeEventListener}
			extraData={extraData}
		>
			{children}
		</UFOThirdPartySegment>
	);
}

export const ForgeUIRenderer = (props: Props): React.JSX.Element => {
	const {
		accountId,
		bridge,
		components,
		consentMessage,
		contextIds,
		contextChangeListeners,
		customBridgeMethods,
		customBridge,
		customBridgeContext,
		dialogs,
		entryPoint,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		hasModalConsentPrompt,
		height,
		initialIframeHeight,
		width,
		isIframeHidden,
		isIframeResizable,
		isInlinePresentation,
		getContextToken,
		loadingComponent,
		locale,
		localId,
		migratedForgeLocalId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onConsentInitialization,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		onInitialRender,
		product,
		timezone,
		getModalWidth,
		onModalExtensionClose,
		onSuccess,
		onAppError,
		viewportDimensions,
		isEmbeddedMacro,
		embeddedParentLocalId,
		isEmbeddedUIKitInCustomUI,
	} = props;

	let cloudId: string | undefined;
	let workspaceId: string | undefined;

	if ('cloudId' in props) {
		cloudId = props.cloudId;
	} else if ('workspaceId' in props) {
		workspaceId = props.workspaceId;
	}

	const cloudOrWorkspaceId = cloudId ? { cloudId } : ({ workspaceId } as { workspaceId: string });

	const { id: extensionId, environmentId, environmentType } = extension;

	useEffect(() => {
		onInitialRender?.();
	}, [onInitialRender]);

	const coreData = useMemo(
		() => ({
			workspaceId,
			cloudId,
			localId: localId || extensionId,
			environmentId,
			environmentType,
			...(migratedForgeLocalId ? { migratedForgeLocalId } : {}),
		}),
		[
			cloudId,
			environmentId,
			environmentType,
			extensionId,
			localId,
			migratedForgeLocalId,
			workspaceId,
		],
	);

	const getThreeLOPrompt = useCallback(
		() => ({
			ThreeLOPrompt:
				modalExtension || hasModalConsentPrompt
					? makeModalThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							isBlanketHidden: true,
							onClose: onConsentModalClose,
						})
					: makeThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							onSuccess: onConsentSuccess,
							message: consentMessage,
							onInitialization: onConsentInitialization,
						}),
		}),
		[
			extension.properties.title,
			consentMessage,
			hasModalConsentPrompt,
			modalExtension,
			onConsentSuccess,
			onConsentModalClose,
			onConsentInitialization,
		],
	);

	const customUIProps = {
		accountId,
		bridge,
		consentMessage,
		contextIds,
		contextChangeListeners,
		coreData,
		customBridgeMethods,
		customBridge,
		customBridgeContext,
		dialogs,
		entryPoint,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		getContextToken,
		getThreeLOPrompt,
		height,
		initialIframeHeight,
		loadingComponent,
		locale,
		localId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		product,
		timezone,
		isIframeHidden,
		isIframeResizable,
		isInlinePresentation,
		isEmbeddedMacro,
		embeddedParentLocalId,
	};

	const uiKitEmbeddedMacroProps = {
		isEmbeddedMacro,
		embeddedParentLocalId,
	};

	const [viewOnClose, setViewOnClose] = useState<() => Promise<void> | void>();

	// this function has known deps listed already in deps array
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const FrameResizable = useCallback(
		makeFrame((inputProps) => {
			const { height, width, dispatch } = inputProps;

			// If height or width is provided by app code, the iframe does not automatically resize to contents
			const isIframeResizable = height || width ? false : true;

			// A Frame resource is supplied at runtime by app code, so it supports the same
			// `<resourceKey>/<entryKey>` multi-entry convention as dynamic modal resources.
			const { resource, resolvedResourceEntry } = resolveDynamicResource(inputProps.resource);

			return (
				<CustomUIRenderer
					{...customUIProps}
					bridge={{
						...customUIProps.bridge,
						getFrameDispatch: () => dispatch,
					}}
					extension={{
						...extension,
						properties: {
							...extension.properties,
							resource,
							resolvedResourceEntry,
						},
					}}
					// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
					entryPoint={undefined}
					// Ensures Frame is always resized to the full height of the content
					// Required because Jira leaves isIframeResizable as false
					isIframeResizable={isIframeResizable}
					height={height}
					width={width}
				/>
			);
		}),
		// need eslint here as re-render only depends on extensionData & extensionPayload
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[customUIProps.extensionData, customUIProps.extensionPayload, extension],
	);

	const ufoSegmentExtraData = useMemo(
		() => {
			const idParts = extensionIdToAnalyticsAttributes(props.extension.id) as ExtensionIdParts;
			return {
				appType: isCustomUI(props.extension, props.entryPoint) ? 'CustomUI' : 'UIKit',
				extensionKey: idParts?.extensionKey,
				appId: idParts?.appId ?? props.extension.appId,
				envId: idParts?.environmentId ?? props.extension.environmentId,
				envType: props.extension.environmentType,
				installationId: props.extension.installationId,
				moduleType: props.extension.type,
				appVersion: props.extension.appVersion,
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			props.extension.id,
			props.extension.environmentId,
			props.extension.environmentType,
			props.extension.installationId,
			props.extension.type,
			props.extension.appVersion,
			props.entryPoint,
		],
	);

	// Derived at render time from the extension + feature gates. ForgeUIRenderer is an ancestor of
	// every runtime terminal (WorkerWebRuntime / InnerIframe v1/v2 via WebRuntime/Iframe), so this
	// single context tags all events fired below it — both renderer-level events (forge.ui.renderer,
	// forge.ui.extension/loaded) and the terminal bridge/app events. Keep getForgeRuntimeType in sync
	// with the gate decisions in WebRuntime and Iframe.
	const runtimeType = getForgeRuntimeType(props.extension, props.entryPoint);

	return (
		<AtlassianRelayEnvironmentProvider>
			<ForgeUIExtensionAnalyticsContext
				extensionId={props.extension.id}
				localId={props.localId || props.extension.id}
			>
				<ForgeUIRuntimeAnalyticsContext runtime={runtimeType}>
					<PerformanceAnalyticsProviderRollout
						profileKey={`${props.extension.id}-${props.extension.key}-${props.entryPoint}`}
						appType={isCustomUI(props.extension, props.entryPoint) ? 'CustomUI' : 'UIKit'}
						forgeEnvironment={props.extension.environmentType}
						extensionType={props.extension.type}
						appVersion={props.extension.appVersion}
					>
						<UFOThirdPartySegmentWithPerfTimeline
							name="forge-ui-app-content-root"
							extraData={ufoSegmentExtraData}
							moduleType={props.extension.type}
							isBackgroundModule={props.isBackgroundModule}
						>
							<PerformanceHoldAttributionAnchorRollout>
								<UserAccessProvider accountId={accountId}>
									{/* hidden span used for testing purposes and to identify that this renderer is being used when toggling the feature flag in products*/}
									<span hidden id="forge-ui-renderer" />
									{!isCustomUI(extension, entryPoint) ? (
										<UIKitRenderer
											coreData={coreData}
											getThreeLOPrompt={getThreeLOPrompt}
											accountId={accountId}
											contextIds={contextIds}
											customBridge={customBridge}
											customBridgeContext={customBridgeContext}
											extension={extension}
											extensionData={extensionData}
											extensionViewData={extensionViewData}
											product={product}
											bridge={{
												// spread down here so product can override the default onClose
												...(modalExtension
													? {
															onClose: (callback) => {
																setViewOnClose(() => callback.data);
																return true;
															},
														}
													: {}),
												...bridge,
											}}
											customBridgeMethods={customBridgeMethods}
											components={{
												Frame: FrameResizable,
												...components,
											}}
											consentMessage={consentMessage}
											entryPoint={entryPoint}
											getContextToken={getContextToken}
											locale={locale}
											localId={localId}
											mentionProvider={mentionProvider}
											modalExtension={modalExtension}
											onConsentSuccess={onConsentSuccess}
											onForgeDocUpdated={onForgeDocUpdated}
											timezone={timezone}
											onConsentModalClose={onConsentModalClose}
											onError={onError}
											loadingComponent={loadingComponent}
											extensionPayload={extensionPayload}
											onSuccess={onSuccess}
											{...cloudOrWorkspaceId}
											onAppError={onAppError}
											viewportDimensions={viewportDimensions}
											viewOnClose={viewOnClose}
											isEmbeddedUIKitInCustomUI={isEmbeddedUIKitInCustomUI}
											contextChangeListeners={contextChangeListeners}
											{...uiKitEmbeddedMacroProps}
										/>
									) : modalExtension ? (
										// getModalWidth not applied here since all modal extensions are defaulted to medium width
										<ModalCustomUIRenderer
											{...customUIProps}
											bridge={{
												...(modalExtension
													? {
															onClose: (callback) => {
																setViewOnClose(() => callback.data);
																// stores the callback for when modal is closed
																return true;
															},
														}
													: {}),
												// spread down here so product can override the default onClose
												...customUIProps.bridge,
											}}
											modalOnClose={onModalExtensionClose}
											closeOnEscape={modalExtension?.shouldCloseOnEscapePress ?? true}
											closeOnOverlayClick={modalExtension?.shouldCloseOnOverlayClick ?? true}
											viewOnClose={viewOnClose}
										/>
									) : (
										<CustomUIRenderer
											{...customUIProps}
											getModalWidth={getModalWidth}
											width={width}
										/>
									)}
								</UserAccessProvider>
							</PerformanceHoldAttributionAnchorRollout>
						</UFOThirdPartySegmentWithPerfTimeline>
					</PerformanceAnalyticsProviderRollout>
				</ForgeUIRuntimeAnalyticsContext>
			</ForgeUIExtensionAnalyticsContext>
		</AtlassianRelayEnvironmentProvider>
	);
};
